import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { IMovement } from '@/common/integrations/menhir/employee/movements/movements.types';
import { IOperationItem } from '@/common/integrations/menhir/employee/operations/operations.types';
import { DateUtils, INTL_MONTH_YEAR_FORMAT } from '@/common/utils/date';
import { ListSection } from '@/web/components/ListSection/ListSection';
import { Flex, Text } from '@coverflex-tech/hypernova';
import { ArrayUtils } from '@coverflex-tech/hypernova-utils';
import { TransactionList } from '../TransactionList/TransactionList';
import { EmptyTransactionsPlaceholder } from '../TransactionList/components/EmptyTransactionsPlaceholder/EmptyTransactionsPlaceholder';

interface ISharedProps {
  hideTitle?: boolean;
}

interface IOperationsProps extends ISharedProps {
  type: 'operations';
  transactions: IOperationItem[];
}

interface IMovementsProps extends ISharedProps {
  type?: 'movements';
  transactions: IMovement[];
}

type IProps = IOperationsProps | IMovementsProps;

const renderTransactionList = (
  type: 'operations' | 'movements',
  transactions: IMovement[] | IOperationItem[]
) => {
  if (type === 'operations')
    return (
      <TransactionList
        type="operations"
        transactions={transactions as IOperationItem[]}
      />
    );

  return (
    <TransactionList
      type="movements"
      transactions={transactions as IMovement[]}
    />
  );
};

export const GroupedTransactionList = ({
  type = 'movements',
  transactions,
  hideTitle,
}: IProps) => {
  const { t } = useTranslation();
  const { employee } = useEmployeeDetails();
  const groupedTransactions = useMemo(() => {
    if (!transactions) {
      return {};
    }
    return ArrayUtils.groupBy(
      ArrayUtils.orderBy<IMovement | IOperationItem>(
        transactions,
        (transaction) => transaction.executedAt,
        'desc'
      ),
      (transaction: IMovement | IOperationItem) =>
        DateUtils.intlFormat(transaction.executedAt, INTL_MONTH_YEAR_FORMAT, {
          language: employee.language,
        })
    );
  }, [transactions, employee]);

  return (
    <ListSection title={!hideTitle ? t('wallet.activity.title') : undefined}>
      <Flex flexDirection="column" gap="xl">
        {Object.keys(groupedTransactions).length > 0 ? (
          Object.keys(groupedTransactions).map((key) => {
            return (
              <Flex flexDirection="column" key={key} gap="md">
                <Text>{key}</Text>
                {renderTransactionList(
                  type,
                  groupedTransactions[key] as IMovement[] | IOperationItem[]
                )}
              </Flex>
            );
          })
        ) : (
          <EmptyTransactionsPlaceholder />
        )}
      </Flex>
    </ListSection>
  );
};
